/**
 * Intent-based prefetch for the orders list route — warms the lazy page chunk
 * and the default All-tab query while the user hovers the sidebar link.
 */
import type { QueryClient } from '@tanstack/react-query';
import { ordersApi } from '@/api/endpoints/orders';
import { DEFAULT_ORDER_LIST_VIEW } from '../hooks/useOrderListFilters';

const ORDER_LIST_PAGE_SIZE = 30;
const DEFAULT_LIST_PARAMS = { view: DEFAULT_ORDER_LIST_VIEW };

let pageChunkPrefetchStarted = false;
let createDrawerChunkPrefetchStarted = false;

function shouldPrefetchOrdersRoute(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(prefers-reduced-data: reduce)').matches) return false;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData) return false;
  return true;
}

/** Starts downloading the lazy OrderListPage chunk once per session. */
export function prefetchOrderListPageChunk(): void {
  if (!shouldPrefetchOrdersRoute() || pageChunkPrefetchStarted) return;
  pageChunkPrefetchStarted = true;
  void import('@/features/orders/pages/OrderListPage');
}

/** Starts downloading the create-order drawer chunk once per session. */
export function prefetchOrderCreateDrawerChunk(): void {
  if (!shouldPrefetchOrdersRoute() || createDrawerChunkPrefetchStarted) return;
  createDrawerChunkPrefetchStarted = true;
  void import('@/features/orders/components/OrderCreateDrawer');
}

/**
 * Prefetches the first page of the default All list so `/orders` can paint
 * rows immediately after the route chunk loads.
 */
export function prefetchOrderListData(queryClient: QueryClient): void {
  if (!shouldPrefetchOrdersRoute()) return;
  void queryClient.prefetchInfiniteQuery({
    queryKey: ['orders', 'infinite', DEFAULT_LIST_PARAMS],
    queryFn: ({ pageParam }) =>
      ordersApi.list({
        ...DEFAULT_LIST_PARAMS,
        limit: ORDER_LIST_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    staleTime: 30_000,
  });
}
